import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import { useFormState } from 'react-hook-form';
import { Labeled, useInput } from 'react-admin';

import Item from './attachment-item';
import Selector from './attachment-selector';
import Uploader from './attachment-uploader';

const unwrapFormError = (error) => {
    if (!error) return null;
    if (typeof error === 'string') return error;

    let { message } = error;
    while (message && typeof message === 'object' && message.message) {
        ({ message } = message);
    }

    return typeof message === 'string' ? message : null;
};

const getFieldMeta = (fieldState, formError, isSubmitted, submitCount) => {
    if (!fieldState && !formError) return null;

    const errorMessage = unwrapFormError(fieldState?.error) || unwrapFormError(formError);

    return {
        touched: fieldState?.isTouched,
        error: errorMessage,
        invalid: fieldState?.invalid,
        isSubmitted,
        submitCount,
    };
};

const shouldShowError = (meta) => {
    if (!meta?.error) return false;

    return Boolean(
        meta.invalid ||
        meta.touched ||
        meta.isSubmitted ||
        meta.submitCount > 0,
    );
};

const isEmptyAttachmentValue = (value) => value === null || value === undefined || value === '';
const attachmentFormat = (value) => (isEmptyAttachmentValue(value) ? null : value);
const attachmentParse = (value) => (isEmptyAttachmentValue(value) ? null : value);

const AttachmentInput = ({
    accept,
    fullWidth = true,
    helperText,
    itemLabel,
    label = 'Attachment',
    hideThumbnail,
    hideButtons,
    isDisabled,
    articleSuitable,
    format = attachmentFormat,
    parse = attachmentParse,
    ...props
}) => {
    const { errors, submitCount, isSubmitted: formIsSubmitted } = useFormState({
        name: props.source,
    });
    const formError = get(errors, props.source);

    let field;
    let fieldState;
    let formState;
    let isRequired;

    try {
        const useInputResult = useInput({
            format,
            parse,
            ...props,
        });
        field = useInputResult.field;
        fieldState = useInputResult.fieldState;
        isRequired = useInputResult.isRequired;
        formState = useInputResult.formState;
    } catch (error) {
        console.warn('AttachmentInput: useInput failed, component may not be in a React Admin form context', error);
        field = null;
        fieldState = null;
        isRequired = false;
    }

    // Create input and meta objects for backward compatibility
    const input = useMemo(() => field ?
        {
            value: field.value,
            onChange: field.onChange,
            onBlur: field.onBlur,
            name: field.name,
        } :
        null, [field]);

    const meta = useMemo(
        () => getFieldMeta(
            fieldState,
            formError,
            formState?.isSubmitted || formIsSubmitted,
            submitCount,
        ),
        [fieldState, formError, formState?.isSubmitted, formIsSubmitted, submitCount],
    );
    const showError = shouldShowError(meta);

    // Debug logging to help identify the issue
    if (!input || !meta) {
        console.error('AttachmentInput: input or meta is undefined', {
            props,
            input,
            meta,
            isRequired,
            hasSource: Boolean(props.source),
            hasResource: Boolean(props.resource),
            sourceValue: props.source,
            resourceValue: props.resource,
        });
    }

    const handleAttachmentsAdded = useCallback((attachment) => {
        if (!field) return;

        if (isArray(attachment)) {
            attachment = attachment.length > 0 ? attachment[0] : null;
        }

        field.onChange(attachment);
        field.onBlur();
    }, [field]);

    const handleAttachmentRemoved = useCallback(() => {
        if (!field) return;

        field.onChange(null);
        field.onBlur();
    }, [field]);

    const handleAttachmentUpdated = useCallback((attachment) => {
        if (!field) return;

        field.onChange(attachment);
        field.onBlur();
    }, [field]);

    return (
        <Labeled
            fullWidth={fullWidth}
            label={label}
            isRequired={isRequired}
            meta={meta || {}}
            input={input || {}}
            resource={props.resource}
            source={props.source}
        >
            <Grid container sx={{ width: '100%' }} spacing={2} >
                {
                    !isDisabled && (
                        <>
                            <Grid size={12}>
                                <Uploader accept={accept} multiple={false} onAttachmentsAdded={handleAttachmentsAdded} />
                            </Grid>
                            <Grid size={12}>
                                <Selector
                                    articleSuitable={articleSuitable}
                                    onAttachmentSelected={handleAttachmentsAdded}
                                />
                            </Grid>
                        </>
                    )
                }
                {
                    field && field.value ?
                        <Grid size={12}>
                            <Item
                                attachment={field.value}
                                label={itemLabel}
                                hideThumbnail={hideThumbnail}
                                hideButtons={hideButtons}
                                onAttachmentRemoved={handleAttachmentRemoved}
                                onAttachmentUpdated={handleAttachmentUpdated}
                            />
                        </Grid> :
                        null
                }
                { showError ? <Grid size={12}><FormHelperText error>{meta.error}</FormHelperText></Grid> : null }
                { helperText ? <Grid size={12}><FormHelperText>{helperText}</FormHelperText></Grid> : null }
            </Grid>
        </Labeled>
    );
};

AttachmentInput.propTypes = {
    isDisabled: PropTypes.bool,
    accept: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    fullWidth: PropTypes.bool,
    helperText: PropTypes.string,
    itemLabel: PropTypes.string,
    label: PropTypes.string,
    hideThumbnail: PropTypes.bool,
    hideButtons: PropTypes.bool,
    resource: PropTypes.string,
    source: PropTypes.string,
};

export default React.memo(AttachmentInput);
